import { Link } from "react-router-dom";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  ShieldCheck,
  ClipboardList,
  BarChart3,
  AlertTriangle,
  ScrollText,
  ArrowRight,
  Trophy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface AdminCard {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  path: string;
  phase?: string;
}

const ADMIN_CARDS: AdminCard[] = [
  {
    id: "wins",
    title: "Wins pipeline",
    description:
      "The achievements band that runs itself — reads news feeds, screens them, publishes what survives. Check it is running, see what it turned away, or run it now.",
    icon: Trophy,
    path: "/admin/wins",
  },
  {
    id: "verification-queue",
    title: "Verification Queue",
    description:
      "Unified moderation for institutions, event organizers, and ref-interest proposals. Approve, reject, request more info.",
    icon: ShieldCheck,
    path: "/admin/verification-queue",
    phase: "1B.3 + 1C.1",
  },
  {
    id: "provider-verifications",
    title: "Provider Verifications",
    description:
      "Review provider trust documents — business licenses, identity, certifications. Approving sets isVerified and rewards the owner.",
    icon: ShieldCheck,
    path: "/admin/provider-verifications",
  },
  {
    id: "claims",
    title: "Ownership Claims",
    description:
      "Review claims from real owners of seeded provider/institution pages. Approving transfers control (provider claimedBy / institution owner).",
    icon: ShieldCheck,
    path: "/admin/claims",
  },
  {
    id: "interests-queue",
    title: "Interests Queue",
    description:
      "Review user-submitted interest proposals (touchstones, spiritual practices, sewa types).",
    icon: ClipboardList,
    path: "/admin/interests-queue",
    phase: "1A",
  },
  {
    id: "analytics",
    title: "Analytics",
    description:
      "Notification performance dashboard + engagement funnel drop-off charts.",
    icon: BarChart3,
    path: "/admin/analytics",
    phase: "1D.1",
  },
  {
    id: "yash-fraud",
    title: "Yash Fraud Queue",
    description:
      "Review flagged Yash transactions; clear false positives or reverse fraudulent awards.",
    icon: AlertTriangle,
    path: "/admin/yash-fraud",
  },
  {
    id: "dharohar-moderation",
    title: "Dharohar Moderation",
    description:
      "Approve or reject submitted heritage content (personalities, art forms, handicrafts).",
    icon: ScrollText,
    path: "/admin/dharohar-moderation",
  },
];

export default function AdminDashboard() {
  return (
    <div className="space-y-8">
      <PageHeader
        title="Admin"
        subtitle="Moderation queues and analytics dashboards. Only visible to users with the admin role."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin" },
        ]}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ADMIN_CARDS.map((card) => (
          <AdminTile key={card.id} card={card} />
        ))}
      </div>
    </div>
  );
}

function AdminTile({ card }: { card: AdminCard }) {
  const Icon = card.icon;
  return (
    <Link
      to={card.path}
      className="block p-5 rounded-2xl border bg-white border-gray-200 hover:border-amber-300 hover:shadow-lg transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 shadow-md shadow-amber-500/20">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 rounded">
          Admin
        </span>
      </div>
      <h3 className="font-semibold text-gray-900 mb-1.5">{card.title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">
        {card.description}
      </p>
      {card.phase && (
        <p className="text-xs text-gray-400 mt-3">Phase {card.phase}</p>
      )}
      <div className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-amber-700">
        Open
        <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}
